import { ReactNode } from 'react';
import { Box, IconButton } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';

import SButton from '../../sbutton';
import { SText } from '../..';
import { TrashIconV2 } from '../../sicons';
import { Type, Types, TypeColor } from '../sconfirm/helper';

// V2-specific overrides (keeps V1 palette untouched).
const V2_ACCENTS: Partial<Record<Type, string>> = {
  delete: '#FA2020'
};

export interface SConfirmDialogV2Props {
  type?: Type;
  title: string;
  description?: string;
  confirmText?: string;
  closeText?: string;
  loading?: boolean;
  onClose(): void;
  onConfirm?: () => void;
}

const ICON_SIZE = 22;

const iconByType = (type: Type): ReactNode => {
  switch (type) {
    case Types.delete:
      return (
        <TrashIconV2
          htmlColor="#fff"
          style={{ width: ICON_SIZE, height: ICON_SIZE }}
        />
      );
    case Types.warning:
      return (
        <WarningRoundedIcon htmlColor="#fff" style={{ fontSize: ICON_SIZE }} />
      );
    case Types.info:
    case Types.infoBlue:
    case Types.regular:
    case Types.none:
    default:
      return (
        <InfoRoundedIcon htmlColor="#fff" style={{ fontSize: ICON_SIZE }} />
      );
  }
};

const SConfirmDialogV2 = ({
  type = Types.delete,
  title,
  description,
  confirmText = 'Confirm',
  closeText = 'Cancel',
  loading,
  onClose,
  onConfirm
}: SConfirmDialogV2Props) => {
  const accent = V2_ACCENTS[type] ?? TypeColor[type];

  return (
    <Box style={{ width: 440, padding: 24, boxSizing: 'border-box' }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12
        }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0
          }}>
          <Box
            style={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: '50%',
              backgroundColor: accent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            {iconByType(type)}
          </Box>
          <SText
            size="lg"
            weight="bold"
            fontColor="#20262D"
            noLineClamp
            title={title}
          />
        </Box>

        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon htmlColor="#5D7083" style={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      {description && (
        <Box style={{ marginTop: 12 }}>
          <SText
            size="sm"
            fontColor="#20262D"
            noLineClamp
            title={description}
          />
        </Box>
      )}

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 16,
          marginTop: 24
        }}>
        {onConfirm && (
          <SButton
            variant="contained"
            loading={loading}
            onClick={onConfirm}
            style={{
              backgroundColor: accent,
              color: '#fff',
              borderRadius: 36,
              padding: '10px 32px',
              fontWeight: 600
            }}>
            {confirmText}
          </SButton>
        )}
        <SButton
          variant="text"
          onClick={onClose}
          style={{ color: '#5D7083', fontWeight: 600 }}>
          {closeText}
        </SButton>
      </Box>
    </Box>
  );
};

export default SConfirmDialogV2;
